import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from '@tanstack/react-router' 
import { Button } from "@/components/ui/button";
import { Loader2, Plus, Pencil, Trash2, GripVertical, FileCode } from "lucide-react";
import { ConfirmDeleteModal } from "@/components/todo/ConfirmDeleteModal";
import { CategoryEditModal } from "@/components/todo/CategoryEditModal";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { API_ADMIN_BASE_URL } from "@/lib/api-client";

interface TodoCategory {
  id: string;
  title: string;
  description: string;
  scopeFiles?: string; // JSON string
  refs?: string; // JSON string
  order: number;
  todoCount: number;
  averageProgress: number;
}

// Sortable Category Card Component
function SortableCategoryCard({
  category,
  onEdit,
  onEditContext,
  onDelete,
  onView,
}: {
  category: TodoCategory;
  onEdit: (cat: TodoCategory) => void;
  onEditContext: (cat: TodoCategory) => void;
  onDelete: (id: string) => void;
  onView: (id: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: category.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Progress badge color
  const getProgressColor = (percent: number) => {
    if (percent === 0) return "bg-red-500";
    if (percent <= 25) return "bg-orange-500";
    if (percent <= 50) return "bg-yellow-500";
    if (percent <= 75) return "bg-blue-500";
    return "bg-green-500";
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group flex items-center justify-between py-2 px-4 border-b hover:bg-muted/50 transition-colors"
    >
      <div className="flex items-center gap-3 flex-1">
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity">
          <GripVertical className="h-3 w-3 text-muted-foreground" />
        </div>
        <div className="w-[30px] text-right flex items-center justify-end">
          <span className={`text-sm font-medium tabular-nums ${category.averageProgress === 100 ? "text-green-600" : "text-muted-foreground"}`}>
            {category.averageProgress}%
          </span>
        </div>
        <div
          className="flex items-center gap-2 flex-1 cursor-pointer"
          onClick={() => onView(category.id)}
        >
          <div className={`h-2 w-2 rounded-full ${getProgressColor(category.averageProgress)}`} />
          <span className="text-sm font-medium hover:text-primary transition-colors">{category.title}</span>
        </div>
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <span>{category.todoCount} items</span>
        </div>
      </div>
      <div className="flex items-center gap-1 ml-4">
        <Button variant="ghost" size="sm" onClick={() => onEdit(category)} className="h-7 px-2" title="Edit title & description">
          <Pencil className="h-3 w-3" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onEditContext(category)} className="h-7 px-2 text-primary" title="Edit context (files & refs)">
          <FileCode className="h-3 w-3" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(category.id)}
          className="text-destructive h-7 px-2"
          title="Delete category"
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/dashboard/todo/categories')({
  component: CategoriesPage,
})

function CategoriesPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<TodoCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // Single unified modal for all category editing
  const [isCategoryEditOpen, setIsCategoryEditOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<TodoCategory | null>(null);

  // Confirmation modal state
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<{ id: string; title: string; todoCount: number } | null>(null);

  // DND sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchCategories();
  }, []);

  async function fetchCategories() {
    try {
      const res = await fetch(`${API_ADMIN_BASE_URL}/api/admin/todo/categories`, {
        credentials: "include",
      });
      const data = await res.json();
      if (data.success) {
        setCategories(data.data);
      }
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    } finally {
      setIsLoading(false);
    }
  }


  function openDeleteConfirmation(id: string) {
    const category = categories.find((c) => c.id === id);
    if (!category) return;

    setCategoryToDelete({
      id: category.id,
      title: category.title,
      todoCount: category.todoCount,
    });
    setIsConfirmDeleteOpen(true);
  }

  async function handleDeleteConfirmed() {
    if (!categoryToDelete) return;

    // If category has todos, just close the modal (it's just an info message)
    if (categoryToDelete.todoCount > 0) {
      setIsConfirmDeleteOpen(false);
      setCategoryToDelete(null);
      return;
    }

    try {
      const res = await fetch(`${API_ADMIN_BASE_URL}/api/admin/todo/categories/${categoryToDelete.id}`, {
        method: "DELETE",
        credentials: "include",
      });

      const data = await res.json();

      if (data.success) {
        fetchCategories();
      } else {
        // Show error if unexpected issue
        alert(data.error || "Failed to delete category");
      }
    } catch (error) {
      console.error("Failed to delete category:", error);
      alert("Failed to delete category");
    } finally {
      setIsConfirmDeleteOpen(false);
      setCategoryToDelete(null);
    }
  }

  function openEditModal(category: TodoCategory | null = null) {
    setEditingCategory(category);
    setIsCategoryEditOpen(true);
  }

  async function handleCategorySave(updates: Partial<TodoCategory>) {
    try {
      const url = editingCategory
        ? `${API_ADMIN_BASE_URL}/api/admin/todo/categories/${editingCategory.id}`
        : `${API_ADMIN_BASE_URL}/api/admin/todo/categories`;

      const method = editingCategory ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(updates),
      });

      const data = await res.json();
      if (data.success) {
        setIsCategoryEditOpen(false);
        setEditingCategory(null);
        fetchCategories();
      }
    } catch (error) {
      console.error("Failed to save category:", error);
    }
  }

  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = categories.findIndex((c) => c.id === active.id);
    const newIndex = categories.findIndex((c) => c.id === over.id);

    const reordered = arrayMove(categories, oldIndex, newIndex);
    setCategories(reordered);

    // Update order in database
    const items = reordered.map((cat, index) => ({
      id: cat.id,
      order: index,
    }));

    try {
      await fetch(`${API_ADMIN_BASE_URL}/api/admin/todo/categories/reorder`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ items }),
      });
    } catch (error) {
      console.error("Failed to reorder categories:", error);
      // Revert on error
      fetchCategories();
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Categories</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage feature categories and their implementation status</p>
        </div>
        <Button onClick={() => openEditModal(null)} size="sm" className="text-xs">
          <Plus className="h-3 w-3 mr-2" />
          New Category
        </Button>
      </div>

      <div className="border rounded-lg overflow-hidden bg-card">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={categories.map((c) => c.id)} strategy={verticalListSortingStrategy}>
            {categories.map((category) => (
              <SortableCategoryCard
                key={category.id}
                category={category}
                onEdit={(cat) => openEditModal(cat)}
                onEditContext={(cat) => openEditModal(cat)}
                onDelete={openDeleteConfirmation}
                onView={(id) => navigate({ to: `/dashboard/todo/${id}` })}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>

      {/* Confirmation Delete Modal */}
      <ConfirmDeleteModal
        isOpen={isConfirmDeleteOpen}
        onClose={() => {
          setIsConfirmDeleteOpen(false);
          setCategoryToDelete(null);
        }}
        onConfirm={handleDeleteConfirmed}
        title="Delete Category"
        description={
          categoryToDelete
            ? categoryToDelete.todoCount > 0
              ? `Cannot delete "${categoryToDelete.title}" because it contains ${categoryToDelete.todoCount} todo item(s). Please delete or move the items first.`
              : `Are you sure you want to delete "${categoryToDelete.title}"? This action cannot be undone.`
            : ""
        }
        confirmText={categoryToDelete && categoryToDelete.todoCount > 0 ? "OK" : "Delete"}
        isDestructive={categoryToDelete ? categoryToDelete.todoCount === 0 : true}
        singleButton={categoryToDelete ? categoryToDelete.todoCount > 0 : false}
      />

      {/* Category Edit Modal - Unified for all editing */}
      <CategoryEditModal
        category={editingCategory}
        isOpen={isCategoryEditOpen}
        onClose={() => {
          setIsCategoryEditOpen(false);
          setEditingCategory(null);
        }}
        onSave={handleCategorySave}
      />
    </div>
  );
}
